/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useState, useEffect, useMemo } from "react";
import {
  GroupsSortFields,
  Maybe,
  SortOrder,
  useGetResourceGroupsLazyQuery,
  useGetResourceGroupsBasicLazyQuery,
} from "~/generated/graphql";

const REQUEST_LIMIT = 50;

type ResourceGroupItem = {
  id: string;
  name: string;
  resourceSelectors?: Maybe<
    {
      id: string;
      resource?: Maybe<{
        id: string;
        name: string;
      }>;
      order: number;
      tagsExpression?: Maybe<string>;
    }[]
  >;
};

type Props = {
  basic?: boolean;
};

export function useGetAllResourceGroups({ basic }: Props) {
  const [resourceGroups, setResourceGroups] = useState<ResourceGroupItem[]>([]);

  const [getDataFull, fullQueryResult] = useGetResourceGroupsLazyQuery({
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const [getDataBasic, basicQueryResult] = useGetResourceGroupsBasicLazyQuery({
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const {
    data: groupsData,
    loading,
    fetchMore,
    error,
    refetch,
  } = basic ? basicQueryResult : fullQueryResult;

  const getGroups = useCallback(
    () =>
      basic
        ? getDataBasic({
            variables: {
              page: 1,
              limit: REQUEST_LIMIT,
              sort: {
                field: GroupsSortFields.Name,
                order: SortOrder.Asc,
              },
            },
          })
        : getDataFull({
            variables: {
              page: 1,
              limit: REQUEST_LIMIT,
              sort: {
                field: GroupsSortFields.Name,
                order: SortOrder.Asc,
              },
            },
          }),
    [basic, getDataBasic, getDataFull]
  );

  const queryResults = useMemo(
    () => groupsData?.groups?.result ?? [],
    [groupsData?.groups?.result]
  );

  useEffect(() => {
    if (resourceGroups.length === 0 && queryResults.length > 0) {
      setResourceGroups(queryResults);
    }
  }, [queryResults, resourceGroups.length]);

  const count = groupsData?.groups?.pageInfo?.count ?? 0;
  const pages = groupsData?.groups?.pageInfo?.pages ?? 0;
  const hasFetchedAll = resourceGroups.length >= count;

  useEffect(() => {
    if (!loading && count > 0 && !hasFetchedAll && pages > 1 && fetchMore) {
      const pageNumbers = Array.from(
        { length: pages - 1 },
        (_, idx) => idx + 2
      );
      const promises = pageNumbers
        .map((page) =>
          fetchMore({
            variables: {
              page,
              limit: REQUEST_LIMIT,
            },
          })
        )
        .filter(Boolean);

      if (promises.length > 0) {
        Promise.all(promises).then((results) => {
          const newGroups = results.flatMap(
            (result) => result?.data?.groups?.result ?? []
          );
          setResourceGroups((prev) => {
            return [...prev, ...newGroups];
          });
          return null;
        });
      }
    }
  }, [
    fetchMore,
    loading,
    count,
    pages,
    hasFetchedAll,
    groupsData?.groups?.result,
  ]);

  return { getGroups, resourceGroups, loading, error, count, refetch } as const;
}
